/**
 * Signatures and the legal footer (piece 32): the company block and the
 * person's block compose into one signature by `position`, the fields are
 * tokens resolved from the Person record, and the legal footer is appended
 * after any signature to every message the application sends — and nothing
 * at all when it is empty.
 */
import { escapeHtml } from '../controls/richText';
import { MAIL_API_PATH } from './client';
import { mailPlainText } from './body';

/** A block with no words and no picture in it, which composes to nothing. */
const blank = (html: string | null | undefined): boolean => !html || (mailPlainText(html).trim() === '' && !/<img\b/i.test(html));

/** The four tokens a signature carries, resolved from the Person record at send, never typed literals. */
export const SIGNATURE_TOKENS = ['name', 'title', 'phone', 'email'] as const;
export type SignatureToken = (typeof SIGNATURE_TOKENS)[number];
export type SignatureFacts = Readonly<Record<SignatureToken, string>>;

export const SIGNATURE_POSITIONS = ['Prefix', 'Append', 'Standalone'] as const;
export type SignaturePositionValue = (typeof SIGNATURE_POSITIONS)[number];

/** The routes the two signature editors read and write: the person's own list, and the company block. */
export const MAIL_SIGNATURE_PATH = `${MAIL_API_PATH}/signatures`;
export const MAIL_SETTINGS_COMPANY_SIGNATURE_PATH = `${MAIL_API_PATH}/settings/company-signature`;

/** 🔒 A person keeps at most this many named signatures (decision 1236, widening the two of decision 0832). */
export const MAX_PERSON_SIGNATURES = 10;
export const MAX_SIGNATURE_NAME = 80;

/** One of the person's own signatures as the editor reads it. */
export interface MailSignatureView {
  id: string;
  name: string;
  bodyHtml: string;
  position: SignaturePositionValue;
  /** A reply's and a forward's signature, rather than a new message's. */
  useOnReplies: boolean;
  /** The mailboxes, by id, whose From address the signature serves; empty serves every address the person sends from. */
  mailboxIds: string[];
}

/** A From address a signature can serve: one the person may send from. */
export interface MailSignatureSender {
  mailboxId: string;
  name: string;
  address: string;
}

export interface MailSignaturesAnswer {
  signatures: MailSignatureView[];
  /** The company block as it stands, which only Settings › Email edits. */
  companyHtml: string;
  senders: MailSignatureSender[];
  /** The person's name, title, phone and email as the tokens resolve today, so the editor previews a signature as a message carries it. */
  facts: SignatureFacts;
}

/** One signature as the editor writes it; an absent id is a new one. The whole list is written at once. */
export interface MailSignatureInput {
  id?: string | null;
  name: string;
  bodyHtml: string;
  position: SignaturePositionValue;
  useOnReplies: boolean;
  mailboxIds: string[];
}

/** The fields the choice of a signature reads. */
export interface SignatureChoiceRow {
  bodyHtml: string;
  position: SignaturePositionValue;
  useOnReplies: boolean;
  mailboxIds: readonly string[];
}

/**
 * 🔒 Which of a person's signatures a message carries (decision 1236): the one
 * naming the From mailbox before the one serving every address. A reply or a
 * forward takes a replies signature where one fits, and the new message's
 * otherwise, as the shorter block always fell back (decision 0832).
 */
export function chooseSignature<T extends SignatureChoiceRow>(rows: readonly T[], use: 'new' | 'reply', mailboxId: string | null): T | null {
  const pick = (replies: boolean): T | null => {
    const fitting = rows.filter((r) => r.useOnReplies === replies && (r.mailboxIds.length === 0 || (mailboxId !== null && r.mailboxIds.includes(mailboxId))));
    return fitting.find((r) => mailboxId !== null && r.mailboxIds.includes(mailboxId)) ?? fitting.find((r) => r.mailboxIds.length === 0) ?? null;
  };
  return use === 'reply' ? (pick(true) ?? pick(false)) : pick(false);
}

/**
 * Why a person's list of signatures is refused, in a sentence, or null. 🔨 Two
 * signatures of one use never serve one From address, and one of each use at
 * most serves every address, so the choice above never has two answers.
 */
export function signatureListProblem(list: readonly MailSignatureInput[], mailboxesAllowed: readonly string[]): string | null {
  if (list.length > MAX_PERSON_SIGNATURES) return `A person keeps at most ${MAX_PERSON_SIGNATURES} signatures.`;
  const names = new Set<string>();
  const served = new Set<string>();
  for (const row of list) {
    const name = row.name.trim();
    if (name === '') return 'Every signature needs a name.';
    if (name.length > MAX_SIGNATURE_NAME) return `A signature's name is ${MAX_SIGNATURE_NAME} characters at most.`;
    if (names.has(name.toLowerCase())) return `Two signatures are both called ${name}; give each its own name.`;
    names.add(name.toLowerCase());
    if (!(SIGNATURE_POSITIONS as readonly string[]).includes(row.position)) return 'A signature sits before the company block, after it, or alone.';
    if (blank(row.bodyHtml)) return `The signature ${name} is empty; write it or remove it.`;
    const use = row.useOnReplies ? 'reply' : 'new';
    for (const id of row.mailboxIds.length === 0 ? ['*'] : row.mailboxIds) {
      if (id !== '*' && !mailboxesAllowed.includes(id)) return `The signature ${name} names an address you do not send from.`;
      const slot = `${use}:${id}`;
      if (served.has(slot)) return id === '*' ? `Two ${use === 'reply' ? 'reply' : 'new-message'} signatures both serve every address; name the addresses each is for.` : `Two ${use === 'reply' ? 'reply' : 'new-message'} signatures serve the same From address; keep one of them for it.`;
      served.add(slot);
    }
  }
  return null;
}


/** 🔨 Each token resolved from the Person record, so a title change updates every signature by itself. */
export function resolveSignatureTokens(html: string, facts: SignatureFacts): string {
  return html.replace(/\{\{\s*(name|title|phone|email)\s*\}\}/g, (_whole, token: SignatureToken) => escapeHtml(facts[token] ?? ''));
}


/**
 * 🔒 One composed signature, never two firing (piece 32): with no personal
 * block the company block stands alone; Prefix puts the person's block before
 * it, Append after it, and Standalone stands alone with no company block.
 */
export function composeSignature(company: string | null, personal: { bodyHtml: string; position: SignaturePositionValue } | null): string {
  const companyBlock = blank(company) ? '' : company!;
  if (!personal || blank(personal.bodyHtml)) return companyBlock;
  if (personal.position === 'Standalone' || !companyBlock) return personal.bodyHtml;
  return personal.position === 'Prefix' ? `${personal.bodyHtml}${companyBlock}` : `${companyBlock}${personal.bodyHtml}`;
}

/** Whether a body is markup, or plain text a message is built from. */
export function isMailHtml(body: string): boolean {
  return /<(p|div|br|table|span|a|strong|em|ul|ol|li|h[1-6]|blockquote)\b/i.test(body);
}

/** The signature as it stands in a composed body, marked so the composer and the suite can find it. */
export function signatureBlockHtml(signature: string): string {
  return blank(signature) ? '' : `<div data-mail-signature="true">${signature}</div>`;
}

/**
 * 🔒 The legal footer after any signature, on every message this application
 * sends (piece 32). 🔨 An empty footer appends nothing at all, not a blank block.
 */
export function appendLegalFooter(body: string, footerHtml: string | null | undefined): string {
  if (blank(footerHtml)) return body;
  if (!isMailHtml(body) && body.trim() !== '') return `${body.replace(/\s+$/, '')}\n\n${mailPlainText(footerHtml!)}`;
  return `${body}<div data-mail-legal-footer="true">${footerHtml}</div>`;
}

/** A body finished for the transport: the signature where it has none yet, then the footer. */
export function finishMailBody(body: string, signature: string, footerHtml: string | null | undefined): string {
  let out = body;
  if (!blank(signature)) out = isMailHtml(body) || body.trim() === '' ? `${body}${signatureBlockHtml(signature)}` : `${body.replace(/\s+$/, '')}\n\n${mailPlainText(signature)}`;
  return appendLegalFooter(out, footerHtml);
}
